import React, {useEffect, useMemo, useState} from 'react'
import useDrivePicker from 'react-google-drive-picker'
import {useDispatch} from 'react-redux'
import {PageTitle} from '../../../../_metronic/layout/core'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'
import {SchedulerModel} from '../models/SchedulerModel'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {
  create,
  selectCurrentUserSetting,
  selectTransactionLoading,
  selectVerifyFolderResult,
  setVerifyFolderResult,
} from '../redux/SchedulerRedux'
import {useFormik} from 'formik'
import {
  SchedulerStatus,
  SchedulerSupportedType,
  SchedulerType,
  weekDays,
} from '../constants'
import {OrderType} from '../../orders/constants'

import {SettingReferenceModel} from '../../setting-reference/models/SettingReferenceModel'
import {
  getAll,
  selectSettingReferenceList,
} from '../../setting-reference/redux/SettingReferenceRedux'
import {SettingReferenceStatus} from '../../setting-reference/constants'
import FolderVerifyStepsModal from './FolderVerifyStepsModal'
import ErrorBoundary from '../../../components/ErrorBoundary'
import {mapFormValuesToCreateRequest} from '../utils'
import {selectUser} from '../../../modules/auth/redux/AuthRedux'
import {PlanType, STORAGE_ADDONS} from '../../pricing/pricing.constant'

const SCHEDULER_PLAN_LIMITS: Record<string, number> = {
  [PlanType.SUBSCRIPTION_BASIC]: 1,
  [PlanType.SUBSCRIPTION_STANDARD]: 1,
  [PlanType.SUBSCRIPTION_PREMIUM]: 5,
}

const initialValues = {
  id: 0,
  name: '',
  status: SchedulerStatus.ACTIVE,
  folderType: SchedulerSupportedType.G_DRIVE,
  folderId: '',
  folderName: '',
  time: '00:00',
  type: SchedulerType.EVERYDAY,
  orderType: OrderType.AI_ONLY,
  settingId: 0,
  verified: false,
  weekDays: [] as string[],
  creditAcknowledged: false, // Add this new field
}

const SchedulerDetail: React.FC<{}> = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const currentUserSetting: SchedulerModel | undefined = useAppSelector(selectCurrentUserSetting)
  const availableSettings: SettingReferenceModel[] = useAppSelector(selectSettingReferenceList)
  const transactionLoading = useAppSelector(selectTransactionLoading)
  const verificationResult = useAppSelector(selectVerifyFolderResult)
  const user = useAppSelector(selectUser)

  const planLimit = user?.planType ? SCHEDULER_PLAN_LIMITS[user.planType] ?? 0 : 0
  const hasStorageAddon = user?.activeAddOns?.some((addon: any) =>
    STORAGE_ADDONS.includes(addon.name)
  )
  const title = currentUserSetting
    ? intl.formatMessage({id: 'Scheduler.UPDATE_TITLE'})
    : intl.formatMessage({id: 'Scheduler.CREATE_TITLE'})
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, intl.formatMessage({id: 'Scheduler.NAME.MIN'}))
      .max(50, intl.formatMessage({id: 'Scheduler.NAME.MAX'}))
      .required(intl.formatMessage({id: 'Scheduler.NAME.REQUIRED'})),
    folderType: Yup.string()
      .oneOf([SchedulerSupportedType.G_DRIVE, SchedulerSupportedType.DROP_BOX])
      .required(intl.formatMessage({id: 'Scheduler.TYPE.REQUIRED'})),
    folderId: Yup.string().required(intl.formatMessage({id: 'Scheduler.FOLDER.REQUIRED'})),
    folderName: Yup.string().required(intl.formatMessage({id: 'Scheduler.FOLDER.REQUIRED'})),
    time: Yup.string().required(intl.formatMessage({id: 'Scheduler.SCHEDULE.TIME.REQUIRED'})),
    type: Yup.string()
      .oneOf([SchedulerType.CUSTOM, SchedulerType.EVERYDAY])
      .required(intl.formatMessage({id: 'Scheduler.SCHEDULE.TYPE.REQUIRED'})),

    orderType: Yup.string()
      .oneOf([OrderType.AI_ONLY, OrderType.PROFESSIONAL])
      .required(intl.formatMessage({id: 'Scheduler.SCHEDULE.ORDER_TYPE.REQUIRED'})),
    settingId: Yup.number()
      .min(1, intl.formatMessage({id: 'Scheduler.AI_SETTING.REQUIRED'}))
      .required(intl.formatMessage({id: 'Scheduler.AI_SETTING.REQUIRED'})),
    verified: Yup.boolean()
      .oneOf([true])
      .required(intl.formatMessage({id: 'Scheduler.FOLDER.VERIFY_REQUIRED'})),
    weekDays: Yup.array().when('type', {
      is: SchedulerType.CUSTOM,
      then: (schema) => schema.min(1, intl.formatMessage({id: 'Scheduler.WEEKDAYS.REQUIRED'})),
    }),
    creditAcknowledged: Yup.boolean()
      .oneOf([true], intl.formatMessage({id: 'Scheduler.CREDIT.REQUIRED'}))
      .required(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      try {
        if (values.id === 0) {
          const createRequest = mapFormValuesToCreateRequest(values)
          dispatch(create(createRequest))
        } else {
          // Handle update case
        }
      } catch (error) {
        setStatus('The creation details is incorrect')
        setSubmitting(false)
      }
    },
  })

  const [showStructureModal, setShowStructureModal] = useState(false)

  const handleConnectFolder = () => {
    dispatch(setVerifyFolderResult(undefined))
    setShowStructureModal(true)
  }

  useEffect(() => {
    dispatch(
      getAll({
        serviceId: 1,
        status: SettingReferenceStatus.ACTIVE,
      })
    )
  }, [])
  useEffect(() => {
    if (verificationResult) {
      formik.setFieldValue('verified', verificationResult.isSuccess)
    } else {
      formik.setFieldValue('verified', false)
    }
  }, [verificationResult])

  // Add handleWeekDayChange function
  const handleWeekDayChange = (day: string) => {
    const currentWeekDays = formik.values.weekDays
    const updatedWeekDays = currentWeekDays.includes(day)
      ? currentWeekDays.filter((d) => d !== day)
      : [...currentWeekDays, day]
    formik.setFieldValue('weekDays', updatedWeekDays)
  }

  // Memoize timezone guidance calculation
  const timezoneGuidance = useMemo(() => {
    const SERVER_TZ = 'Asia/Ho_Chi_Minh'
    const localTzName = Intl.DateTimeFormat().resolvedOptions().timeZone

    // Check if timezones differ by comparing offsets
    const now = new Date()
    const serverStr = now.toLocaleString('en-US', {timeZone: SERVER_TZ})
    const localStr = now.toLocaleString('en-US')
    const diffMs = new Date(localStr).getTime() - new Date(serverStr).getTime()

    if (Math.abs(diffMs) < 60000) return null // Same timezone, don't show

    const [h, m] = (formik.values.time || '00:00').split(':').map(Number)
    const serverMinutes = h * 60 + m
    const localMinutes = ((serverMinutes + Math.round(diffMs / 60000)) % 1440 + 1440) % 1440
    const localHour = Math.floor(localMinutes / 60).toString().padStart(2, '0')
    const localMin = (localMinutes % 60).toString().padStart(2, '0')

    return {
      serverTz: SERVER_TZ,
      localTzName,
      localHour,
      localMin,
    }
  }, [formik.values.time])

  return (
    <>
      <PageTitle breadcrumbs={[]}>{title}</PageTitle>
      <div className='card mb-10'>
        <form
          id='kt_user_scheduler_setting_form'
          onSubmit={formik.handleSubmit}
          noValidate
          className='form'
        >
          <div className='card-body border-top p-9'>
            {/* Plan Limits & Requirements Notice */}
            <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-6 mb-10'>
              <i className='fas fa-info-circle text-primary fs-2 me-4 mt-1'></i>
              <div className='d-flex flex-column'>
                <span className='fs-7 text-gray-700 mb-2'>
                  {intl.formatMessage({id: 'Scheduler.PLAN_LIMITS.FEATURE_DESC'})}
                </span>
                <span className='fs-7 text-gray-700 mb-2'>
                  {intl.formatMessage(
                    {id: 'Scheduler.PLAN_LIMITS.PLAN_NOTE'},
                    {limit: planLimit, maxLimit: 5}
                  )}
                  {!hasStorageAddon && (
                    <span className='text-danger fw-semibold ms-1'>
                      {intl.formatMessage({id: 'Scheduler.PLAN_LIMITS.STORAGE_ADDON_REQUIRED'})}
                    </span>
                  )}
                </span>
                <span className='fs-7 text-gray-700 mb-2'>
                  {intl.formatMessage({id: 'Scheduler.PLAN_LIMITS.FOLDER_UNIQUE'})}
                </span>
                <span className='fs-7 text-gray-700'>
                  {intl.formatMessage({id: 'Scheduler.PLAN_LIMITS.TIMEZONE_NOTE'})}
                </span>
              </div>
            </div>

            {/* Service Field */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.SERVICES'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder={intl.formatMessage({id: 'Scheduler.SERVICE.PLACEHOLDER'})}
                  disabled={true}
                  value={intl.formatMessage({id: 'SERVICE.SERVICE.image_enhance'})}
                />
              </div>
            </div>
            {/* Name Field */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.NAME'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <input
                  type='text'
                  className='form-control form-control-lg form-control-solid'
                  placeholder={intl.formatMessage({id: 'Scheduler.NAME.PLACEHOLDER'})}
                  {...formik.getFieldProps('name')}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Folder Type Selection */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.FOLDER_TYPE'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <div className='d-flex align-items-center'>
                  <div className='form-check form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      id='folderTypeGDrive'
                      {...formik.getFieldProps('folderType')}
                      checked={formik.values.folderType === SchedulerSupportedType.G_DRIVE}
                    />
                    <label className='form-check-label' htmlFor='folderTypeGDrive'>
                      Google Drive
                    </label>
                  </div>
                  <div className='form-check form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='radio'
                      id='folderTypeDropbox'
                      disabled={true}
                      checked={formik.values.folderType === SchedulerSupportedType.DROP_BOX}
                      {...formik.getFieldProps('folderType')}
                    />
                    <label className='form-check-label' htmlFor='folderTypeDropbox'>
                      Dropbox ({intl.formatMessage({id: 'GENERAL.COMING'})})
                    </label>
                  </div>
                </div>
                {formik.touched.folderType && formik.errors.folderType && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.folderType}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Schedule Type */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.TYPE'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <div className='d-flex align-items-center'>
                  <div className='form-check form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      id='typeEveryday'
                      checked={formik.values.type === SchedulerType.EVERYDAY}
                      name='type'
                      onChange={() => {
                        formik.setFieldValue('type', SchedulerType.EVERYDAY)
                      }}
                    />
                    <label className='form-check-label' htmlFor='typeEveryday'>
                      {intl.formatMessage({id: 'Scheduler.TYPE.EVERYDAY'})}
                    </label>
                  </div>
                  <div className='form-check form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='radio'
                      disabled={true}
                      id='typeCustom'
                      checked={formik.values.type === SchedulerType.CUSTOM}
                      name='type'
                      onChange={() => {
                        formik.setFieldValue('type', SchedulerType.CUSTOM)
                      }}
                    />
                    <label className='form-check-label' htmlFor='typeCustom'>
                      {intl.formatMessage({id: 'Scheduler.TYPE.CUSTOM'})}(
                      {intl.formatMessage({id: 'GENERAL.COMING'})})
                    </label>
                  </div>
                </div>
                {formik.touched.type && formik.errors.type && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.type}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Schedule Time */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.TIME'})}
              </label>
              <div className='col-lg-9 fv-row'>
                {formik.values.type === SchedulerType.CUSTOM && (
                  <>
                    <div className='mb-5'>
                      <div className='fw-bold mb-2'>
                        {intl.formatMessage({id: 'Scheduler.WEEKDAYS'})}
                      </div>
                      <div
                        className='d-grid gap-2'
                        style={{gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))'}}
                      >
                        {weekDays.map((day) => (
                          <div
                            key={day.value}
                            className='form-check form-check-custom form-check-solid form-check-sm'
                          >
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id={`weekday-${day.value}`}
                              checked={formik.values.weekDays.includes(day.value)}
                              onChange={() => handleWeekDayChange(day.value)}
                            />
                            <label
                              className='form-check-label ms-2'
                              htmlFor={`weekday-${day.value}`}
                            >
                              {intl.formatMessage({id: day.label})}
                            </label>
                          </div>
                        ))}
                      </div>
                      {formik.touched.weekDays && formik.errors.weekDays && (
                        <div className='fv-plugins-message-container mt-2'>
                          <span role='alert' className='text-danger'>
                            {formik.errors.weekDays}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className='mb-3'>
                  <div className='fw-bold mb-2'>
                    {intl.formatMessage({id: 'Scheduler.EXECUTION_TIME'})}
                  </div>

                  <div className='d-flex align-items-center'>
                    {/* Hour stepper */}
                    <div className='d-flex flex-column align-items-center me-2'>
                      <span className='text-gray-500 fs-8 mb-1'>
                        {intl.formatMessage({id: 'Scheduler.TIME.HOUR'})}
                      </span>
                      <div className='input-group input-group-solid' style={{width: '100px'}}>
                        <button
                          type='button'
                          className='btn btn-icon btn-light-primary btn-sm'
                          onClick={() => {
                            const currentHour = parseInt((formik.values.time || '00:00').split(':')[0])
                            const currentMinute = (formik.values.time || '00:00').split(':')[1]
                            const newHour = ((currentHour - 1 + 24) % 24).toString().padStart(2, '0')
                            formik.setFieldValue('time', `${newHour}:${currentMinute}`)
                          }}
                        >
                          <i className='fas fa-minus fs-7'></i>
                        </button>
                        <input
                          type='text'
                          className='form-control form-control-solid text-center fw-bold fs-4 px-0'
                          value={(formik.values.time || '00:00').split(':')[0]}
                          readOnly
                        />
                        <button
                          type='button'
                          className='btn btn-icon btn-light-primary btn-sm'
                          onClick={() => {
                            const currentHour = parseInt((formik.values.time || '00:00').split(':')[0])
                            const currentMinute = (formik.values.time || '00:00').split(':')[1]
                            const newHour = ((currentHour + 1) % 24).toString().padStart(2, '0')
                            formik.setFieldValue('time', `${newHour}:${currentMinute}`)
                          }}
                        >
                          <i className='fas fa-plus fs-7'></i>
                        </button>
                      </div>
                    </div>

                    {/* Separator */}
                    <span className='fw-bolder fs-2 text-gray-400 mx-2 mt-4'>:</span>

                    {/* Minute stepper */}
                    <div className='d-flex flex-column align-items-center ms-2'>
                      <span className='text-gray-500 fs-8 mb-1'>
                        {intl.formatMessage({id: 'Scheduler.TIME.MINUTE'})}
                      </span>
                      <div className='input-group input-group-solid' style={{width: '100px'}}>
                        <button
                          type='button'
                          className='btn btn-icon btn-light-primary btn-sm'
                          onClick={() => {
                            const currentHour = (formik.values.time || '00:00').split(':')[0]
                            const currentMinute = parseInt((formik.values.time || '00:00').split(':')[1])
                            const newMinute = ((currentMinute - 10 + 60) % 60).toString().padStart(2, '0')
                            formik.setFieldValue('time', `${currentHour}:${newMinute}`)
                          }}
                        >
                          <i className='fas fa-minus fs-7'></i>
                        </button>
                        <input
                          type='text'
                          className='form-control form-control-solid text-center fw-bold fs-4 px-0'
                          value={(formik.values.time || '00:00').split(':')[1]}
                          readOnly
                        />
                        <button
                          type='button'
                          className='btn btn-icon btn-light-primary btn-sm'
                          onClick={() => {
                            const currentHour = (formik.values.time || '00:00').split(':')[0]
                            const currentMinute = parseInt((formik.values.time || '00:00').split(':')[1])
                            const newMinute = ((currentMinute + 10) % 60).toString().padStart(2, '0')
                            formik.setFieldValue('time', `${currentHour}:${newMinute}`)
                          }}
                        >
                          <i className='fas fa-plus fs-7'></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Timezone guidance — only show if user's timezone differs from server */}
                  {timezoneGuidance && (
                    <div className='notice d-flex bg-light-info rounded border-info border border-dashed p-4 mt-4'>
                      <i className='fas fa-globe text-info fs-4 me-3 mt-1'></i>
                      <div className='d-flex flex-column'>
                        <span className='fs-7 text-gray-700'>
                          {intl.formatMessage(
                            {id: 'Scheduler.TIME.TIMEZONE_NOTICE'},
                            {timezone: timezoneGuidance.serverTz}
                          )}
                        </span>
                        <span className='fs-7 fw-bold text-dark mt-1'>
                          {formik.values.time || '00:00'} ({timezoneGuidance.serverTz}) = {timezoneGuidance.localHour}:{timezoneGuidance.localMin} ({timezoneGuidance.localTzName})
                        </span>
                        <span className='fs-8 text-gray-500 mt-1'>
                          {intl.formatMessage({id: 'Scheduler.TIME.TIMEZONE_TIP'})}
                        </span>
                      </div>
                    </div>
                  )}

                  {formik.touched.time && formik.errors.time && (
                    <div className='fv-plugins-message-container mt-2'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.time}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Settings Selection */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.SETTING'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <select
                  className='form-select form-select-solid form-select-lg'
                  {...formik.getFieldProps('settingId')}
                >
                  <option value={0}>{intl.formatMessage({id: 'SYSTEM.SELECT'})}</option>
                  {availableSettings
                    ?.filter((setting) => setting.isAiSupport)
                    .map((setting: SettingReferenceModel) => (
                      <option key={setting.id} value={setting.id}>
                        {setting.name}
                      </option>
                    ))}
                </select>
                {formik.touched.settingId && formik.errors.settingId && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.settingId}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Order Type */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.ORDER_TYPE'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <div className='d-flex align-items-center'>
                  <div className='form-check form-check-custom form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      type='radio'
                      id='orderTypeAI'
                      checked={formik.values.orderType === OrderType.AI_ONLY}
                      name='orderType'
                      onChange={() => {
                        formik.setFieldValue('orderType', OrderType.AI_ONLY)
                      }}
                    />
                    <label className='form-check-label' htmlFor='orderTypeAI'>
                      {intl.formatMessage({id: 'ORDER.TYPE.AI_ONLY'})}
                    </label>
                  </div>
                  <div className='form-check form-check-custom form-check-solid'>
                    <input
                      className='form-check-input'
                      type='radio'
                      id='orderTypePro'
                      disabled={true}
                      checked={formik.values.orderType === OrderType.PROFESSIONAL}
                      name='orderType'
                      onChange={() => {
                        formik.setFieldValue('orderType', OrderType.PROFESSIONAL)
                      }}
                    />
                    <label className='form-check-label' htmlFor='orderTypePro'>
                      {intl.formatMessage({id: 'ORDER.TYPE.PROFESSIONAL'})}(
                      {intl.formatMessage({id: 'GENERAL.COMING'})})
                    </label>
                  </div>
                </div>
                {formik.touched.orderType && formik.errors.orderType && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.orderType}</span>
                  </div>
                )}
              </div>
            </div>
            {/* Folder Selection */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.FOLDER'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <div className='d-flex align-items-center'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid me-3'
                    placeholder={intl.formatMessage({id: 'Scheduler.FOLDER.PLACEHOLDER'})}
                    {...formik.getFieldProps('folderName')}
                    readOnly
                  />
                  <button
                    type='button'
                    className='btn btn-primary me-2 text-nowrap'
                    onClick={handleConnectFolder}
                    disabled={formik.values.folderType !== SchedulerSupportedType.G_DRIVE}
                  >
                    {formik.values.folderId
                      ? intl.formatMessage({id: 'Scheduler.FOLDER.CHANGE'})
                      : intl.formatMessage({id: 'Scheduler.FOLDER.CONNECT'})}
                  </button>
                </div>
                {formik.touched.folderName && formik.errors.folderName && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.folderName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Verification Status */}
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>
                {intl.formatMessage({id: 'Scheduler.FOLDER.VERIFY_STATUS'})}
              </label>
              <div className='col-lg-9 fv-row'>
                <div className='d-flex align-items-center'>
                  <span
                    className={`badge badge-${formik.values.verified ? 'success' : 'warning'} me-3`}
                  >
                    {formik.values.verified
                      ? intl.formatMessage({id: 'Scheduler.FOLDER.VERIFIED'})
                      : intl.formatMessage({id: 'Scheduler.FOLDER.NOT_VERIFIED'})}
                  </span>
                </div>
                {formik.touched.verified && formik.errors.verified && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert'>{formik.errors.verified}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Credit Usage Warning */}
            <div className='separator separator-dashed my-10'></div>

            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label fw-bold fs-6 text-danger'>
                {intl.formatMessage({id: 'Scheduler.CREDIT.WARNING.TITLE'})}
              </label>
              <div className='col-lg-9'>
                <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
                  <span className='svg-icon svg-icon-2tx svg-icon-warning me-4'>
                    <i className='fas fa-exclamation-triangle fs-2'></i>
                  </span>
                  <div className='d-flex flex-stack flex-grow-1'>
                    <div>
                      <h4 className='text-dark fw-bold mb-3'>
                        {intl.formatMessage({id: 'Scheduler.CREDIT.WARNING.SUBTITLE'})}
                      </h4>
                      <div className='fs-6 text-gray-700'>
                        <p className='mb-2'>
                          {intl.formatMessage({id: 'Scheduler.CREDIT.WARNING.DESCRIPTION1'})}
                        </p>
                        <p className='mb-2'>
                          {intl.formatMessage({id: 'Scheduler.CREDIT.WARNING.DESCRIPTION2'})}
                        </p>
                        <p className='mb-0'>
                          {intl.formatMessage({id: 'Scheduler.CREDIT.WARNING.DESCRIPTION3'})}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='form-check form-check-custom form-check-solid mt-5'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='creditAcknowledged'
                    checked={formik.values.creditAcknowledged}
                    onChange={(e) => formik.setFieldValue('creditAcknowledged', e.target.checked)}
                  />
                  <label className='form-check-label fw-semibold' htmlFor='creditAcknowledged'>
                    {intl.formatMessage({id: 'Scheduler.CREDIT.WARNING.ACKNOWLEDGE'})}
                  </label>
                </div>
                {formik.touched.creditAcknowledged && formik.errors.creditAcknowledged && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.creditAcknowledged}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button
              type='button'
              className='btn btn-light btn-active-light-primary me-2'
              onClick={() => {
                /* Add your cancel handler here */
              }}
            >
              {intl.formatMessage({id: 'SYSTEM.CANCEL'})}
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={transactionLoading || !formik.isValid}
            >
              {intl.formatMessage({id: 'SYSTEM.SAVE'})}
            </button>
          </div>
        </form>
      </div>
      <ErrorBoundary
        fallback={
          <div className='modal show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
            <div className='modal-dialog modal-dialog-centered'>
              <div className='modal-content'>
                <div className='modal-body text-center p-10'>
                  <i className='fas fa-exclamation-triangle text-warning fs-3x mb-5'></i>
                  <h4 className='mb-3'>{intl.formatMessage({id: 'MESSAGE.ERROR.SOMETHING_WRONG'})}</h4>
                  <p className='text-muted mb-5'>
                    {intl.formatMessage({id: 'Scheduler.FOLDER.PICKER_ERROR'})}
                  </p>
                  <button
                    className='btn btn-primary'
                    onClick={() => setShowStructureModal(false)}
                  >
                    {intl.formatMessage({id: 'SYSTEM.MODAL.CLOSE'})}
                  </button>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <FolderVerifyStepsModal
          show={showStructureModal}
          onHide={() => setShowStructureModal(false)}
          onCompleted={(result: any) => {
            setShowStructureModal(false)
            if (result.isSuccess) {
              formik.setFieldValue('folderId', result.folderId)
              formik.setFieldValue('folderName', result.folderName)
            } else {
              formik.setFieldValue('folderId', '')
              formik.setFieldValue('folderName', '')
            }
          }}
        />
      </ErrorBoundary>
    </>
  )
}

export default SchedulerDetail
